import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  CREATE_DELIVERY_MUTATION,
  UPDATE_DELIVERY_MUTATION,
  GET_DELIVERIES_QUERY
} from "../../graphql/orderMutation";
import client from "../../graphql/client"; // adjust path to your Apollo client

const env = process.env.REACT_APP_ENV_SETTINGS || "dev";

// -----------------------------
// Types
// -----------------------------
interface Delivery {
  id: string;
  orderId: string;
  driverId?: string;
  vehicleId?: string;
  status: string;
  location?: any;
}

interface DeliveryState {
  deliveries: Delivery[];
  loading: boolean;
  error: string | null;
}

// -----------------------------
// Initial State
// -----------------------------
const initialState: DeliveryState = {
  deliveries: [],
  loading: false,
  error: null,
};

// -----------------------------
// Async Thunks
// -----------------------------
export const updateDelivery = createAsyncThunk(
  "deliveries/updateDelivery",
  async ({
    id,
    status,
    location,
    trackId,
  }: { id: string; status: string; location?: any; trackId?: string }) => {
    if (env === "dev") {
      return { id, status, location, trackId };
    } else {
      const response = await client.mutate({
        mutation: UPDATE_DELIVERY_MUTATION,
        variables: { id, status, location, trackId }, // ✅ pass trackId
      });
      return response.data.updateDelivery;
    }
  }
);

export const createDelivery = createAsyncThunk(
  "deliveries/createDelivery",
  async ({
    orderId,
    driverId,
    vehicleId,
    status,
    location,
    trackId,
  }: {
    orderId: string;
    driverId?: string;
    vehicleId?: string;
    status: string;
    location?: any;
    trackId?: string;
  }) => {
    if (env === "dev") {
      return {
        id: `del_${Math.random().toString(36).substr(2, 9)}`,
        orderId,
        driverId,
        vehicleId,
        status,
        location,
        trackId,
      };
    } else {
      const response = await client.mutate({
        mutation: CREATE_DELIVERY_MUTATION,
        variables: { orderId, driverId, vehicleId, status, location, trackId }, // ✅ pass trackId
      });
      return response.data.createDelivery;
    }
  }
);


export const getDeliveries = createAsyncThunk(
  "deliveries/getDeliveries",
  async (orderId: string) => {
    const response = await client.query({
      query: GET_DELIVERIES_QUERY,
      variables: { orderId },
      fetchPolicy: "no-cache",
    });
    return response.data.getDeliveries;
  }
);



// -----------------------------
// Slice
// -----------------------------
const deliverySlice = createSlice({
  name: "deliveries",
  initialState,
  reducers: {
    resetError: (state) => {
      state.error = null;
    },
    clearDeliveries: (state) => {
      state.deliveries = [];
    },
  },
  extraReducers: (builder) => {
    // Create

    // deliverySlice.ts

    // Create
    builder
      .addCase(createDelivery.pending, (state) => {
        state.loading = true;
      })
      .addCase(createDelivery.fulfilled, (state, action: PayloadAction<Delivery>) => {
        state.loading = false;
        state.deliveries.push(action.payload);
      })
      .addCase(createDelivery.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to create delivery";
      });

    // Update
    builder
      .addCase(updateDelivery.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateDelivery.fulfilled, (state, action: PayloadAction<Delivery>) => {
        state.loading = false;
        const index = state.deliveries.findIndex((d) => d.id === action.payload.id);
        if (index !== -1) {
          state.deliveries[index] = action.payload;
        }
      })
      .addCase(updateDelivery.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to update delivery";
      });

    // builder
    //   .addCase(createDelivery.fulfilled, (state, action: PayloadAction<Delivery>) => {
    //     state.deliveries.push(action.payload);
    //   })
    //   .addCase(createDelivery.rejected, (state, action) => {
    //     state.error = action.error.message || "Failed to create delivery";
    //   });

    // // Update
    // builder
    //   .addCase(updateDelivery.fulfilled, (state, action: PayloadAction<Delivery>) => {
    //     const index = state.deliveries.findIndex((d) => d.id === action.payload.id);
    //     if (index !== -1) {
    //       state.deliveries[index] = action.payload;
    //     }
    //   })
    //   .addCase(updateDelivery.rejected, (state, action) => {
    //     state.error = action.error.message || "Failed to update delivery";
    //   });

    // Get
    builder
      .addCase(getDeliveries.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getDeliveries.fulfilled, (state, action: PayloadAction<Delivery[]>) => {
        state.loading = false;
        state.deliveries = action.payload || [];
      })
      .addCase(getDeliveries.rejected, (state, action) => {
        state.loading = false;
        state.deliveries = [];
        state.error = action.error.message || "Failed to fetch deliveries";
      });
  },
});

// -----------------------------
// Exports
// -----------------------------
export const { resetError, clearDeliveries } = deliverySlice.actions;
export default deliverySlice.reducer;
